import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../shared/api/client';
import { useAuthStore } from '../shared/store/authStore';
import { PageHeader } from '../shared/ui/PageHeader';
import { Card } from '../shared/ui/Card';

export function ReportsPage() {
  const { t } = useTranslation();
  const canRead = useAuthStore((s) => s.hasPermission('reports:read'));
  const canExport = useAuthStore((s) => s.hasPermission('reports:export'));

  const occupancy = useQuery({
    queryKey: ['reports-occupancy'],
    queryFn: () => api.occupancyReport(),
    enabled: canRead,
  });
  const revenue = useQuery({
    queryKey: ['reports-revenue'],
    queryFn: () => api.revenueReport(),
    enabled: canRead,
  });
  const insights = useQuery({
    queryKey: ['ai-insights'],
    queryFn: () => api.listAiInsights(),
    enabled: canRead,
  });

  const exportMut = useMutation({
    mutationFn: (format: 'csv' | 'xlsx' | 'pdf') => api.exportSessionsReport(format),
  });
  const generateAi = useMutation({
    mutationFn: () => api.generateAiInsight({ kind: 'occupancy_forecast' }),
    onSuccess: async () => {
      await insights.refetch();
    },
  });

  if (!canRead) {
    return <div className="alert alert-warning">{t('reports.forbidden')}</div>;
  }

  const occ = occupancy.data?.data;
  const rev = revenue.data?.data;
  const insightRows = insights.data?.data ?? [];

  return (
    <>
      <PageHeader
        actions={
          canExport ? (
            <>
              <button type="button" className="btn btn-light" onClick={() => exportMut.mutate('csv')}>
                CSV
              </button>
              <button type="button" className="btn btn-light" onClick={() => exportMut.mutate('xlsx')}>
                Excel
              </button>
              <button type="button" className="btn btn-light" onClick={() => exportMut.mutate('pdf')}>
                PDF
              </button>
            </>
          ) : undefined
        }
      />

      <div className="row g-4 mb-4">
        <div className="col-md-6">
          <Card title={t('reports.occupancy')}>
            {occ ? (
              <p className="mb-0">
                {occ.occupiedSpots}/{occ.totalSpots} ({(occ.occupancyRate * 100).toFixed(1)}%) ·{' '}
                {t('reports.openSessions')}: {occ.openSessions}
              </p>
            ) : (
              <p className="text-muted mb-0">…</p>
            )}
          </Card>
        </div>
        <div className="col-md-6">
          <Card title={t('reports.revenue')}>
            {rev ? (
              <p className="mb-0">
                {(rev.totalCents / 100).toFixed(2)} {rev.currency} · {rev.invoiceCount}{' '}
                {t('reports.invoices')} · {(rev.paidCents / 100).toFixed(2)} {t('reports.paid')}
              </p>
            ) : (
              <p className="text-muted mb-0">…</p>
            )}
          </Card>
        </div>
      </div>

      {exportMut.data && (
        <div className="alert alert-success">
          {t('reports.exported')}: {exportMut.data.data.relativePath} ({exportMut.data.data.bytes}{' '}
          B)
        </div>
      )}

      <Card
        title={t('reports.ai')}
        toolbar={
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => generateAi.mutate()}
            disabled={generateAi.isPending}
          >
            {t('reports.generateAi')}
          </button>
        }
      >
        <div className="d-grid gap-3">
          {insightRows.map((i) => (
            <div key={i.id}>
              <div className="fw-semibold">
                {i.title} ({i.score})
              </div>
              <div className="text-muted small">{i.summary}</div>
            </div>
          ))}
          {insightRows.length === 0 && (
            <p className="text-muted mb-0">{t('reports.noInsights')}</p>
          )}
        </div>
      </Card>
    </>
  );
}
